import {
  BooleanField,
  createJaNeinField,
  DateField,
  DependencyFunction,
  FieldType,
  IModelContext,
  isFrageTrue,
  ModelField,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  ModelOption,
  NumberField,
  requiredValueOptionsValidation,
  StringField,
  ValueOptions
} from '@mng/model';
import {
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_AKTUELLE_KRANKHEITEN_WELCHE_BEHANDELT,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_KIND_SONSTIGE_KRANKHEITEN_WELCHE_AUSGEHEILT,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_KRANKHEITEN_WELCHE_BEHANDELT,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_SCHWERE_KRANKHEITEN_WELCHE_BEHANDELT,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_STATIONAER_WELCHE_AUSGEHEILT,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_VERLETZUNGEN_WELCHE_INVALIDITAET,
  ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_VERLETZUNGEN_WELCHE_UNFALL_FOLGEN
} from '../../antrag.model.paths';
import { Gesundheitsfragen } from '../../antrag.model.types';
import { isFragengruppeGvSparteRequired } from './gesundheitsfragen-dependencies';
import {
  createOptionJaDependencyFunction,
  isPersonImErstenLebensjahrDependencyFunction,
  isPersonNotImErstenLebensjahrDependencyFunction,
  isPersonWeiblichDependencyFunction,
  notHasPersonTarifklasseAssistedCareBestDependencyFunction
} from './gv-gesundheitsfragen.dependencies';

// for transition period
export function setGvGesundheitsFragenBooleanTypeObject(newGvGesundheitsFragenBooleanTypeObject: {
  [id: string]: boolean;
}) {
  gvGesundheitsFragenBooleanTypeObject = newGvGesundheitsFragenBooleanTypeObject;
}
export function getGvGesundheitsFragenBooleanTypeObject(): { [id: string]: boolean } {
  return gvGesundheitsFragenBooleanTypeObject;
}

export const stationaerOption: ModelOption = { name: 'Stationär', value: 1 };
export const ambulantOption: ModelOption = { name: 'Ambulant', value: 2 };
export const stationaerAmbulantOptions: ValueOptions = new ValueOptions([stationaerOption, ambulantOption]);

let gvGesundheitsFragenBooleanTypeObject: { [id: string]: boolean };

export function isGvGesundheitsFrageBooleanType(id: string) {
  if (gvGesundheitsFragenBooleanTypeObject) {
    return gvGesundheitsFragenBooleanTypeObject[id];
  } else {
    console.error('GV Gesundheitsfragen sind noch nicht initialisiert -> createGvBooleanGesundheitsFragen');
  }
}

export function createGvGesundheitsFragen2026(): ModelItem[] {
  return [...createDioptrienModelFields()];
}

export function createGvBooleanGesundheitsFragen2026(): ModelItem[] {
  const gvModelItems: ModelItem[] = [
    ...createGvAktuelleKrankheitenModelItems(),
    createGvFruehgeburtModelField(),
    ...createGvKleinkindFrageModelItems(),
    ...createGvBehandlungenGeplantModelItems(),
    ...createGvSuchtgifteFrageModelItems(),
    ...createGvSchwangerschaftModelItems(),
    ...createGvKrankheitenModelItems(),
    ...createGvStationaerModelItems(),
    ...createGvVerletzungenModelItems(),
    ...createGvSchwereKrankheitenModelItems(),
    ...createGvPflegegeldModelItems(),
    ...createGvSportModelItems()
  ];

  if (!gvGesundheitsFragenBooleanTypeObject) {
    const newGvGesundheitsFragenBooleanTypeObject: { [id: string]: boolean } = {};
    gvModelItems.forEach((modelItem: ModelItem) => {
      if (modelItem instanceof ModelField && FieldType.BOOLEAN === modelItem.type) {
        newGvGesundheitsFragenBooleanTypeObject[modelItem.id] = true;
      }
    });
    gvGesundheitsFragenBooleanTypeObject = newGvGesundheitsFragenBooleanTypeObject;
    setGvGesundheitsFragenBooleanTypeObject(gvGesundheitsFragenBooleanTypeObject);
  }

  return gvModelItems;
}

/**
 * FRAGE: DIOPTRIEN
 *
 */
export function createDioptrienModelFields(): ModelField[] {
  return [
    new NumberField('gvDioptrienLinks', 'Dioptrien links')
      .withNumberFieldOptions({ allowDecimal: true, allowNegative: true })
      .withMinvalue(-99)
      .withMaxvalue(99)
      .withFunctionDependencies([isFragengruppeGvSparteRequired, isPersonNotImErstenLebensjahrDependencyFunction]),
    new NumberField('gvDioptrienRechts', 'Dioptrien rechts')
      .withMinvalue(-99)
      .withMaxvalue(99)
      .withNumberFieldOptions({ allowDecimal: true, allowNegative: true })
      .withFunctionDependencies([isFragengruppeGvSparteRequired, isPersonNotImErstenLebensjahrDependencyFunction])
  ];
}

/**
 * FRAGE: BEHANDLUNGEN / OPERATIONEN GEPLANT
 *
 */
export function createGvBehandlungenGeplantModelItems(): ModelItem[] {
  const behandlungen: BooleanField = new BooleanField(
    'gvBehandlungenGeplant',
    'Sind Behandlungen (auch Operationen, Untersuchungen, Physiotherapien, Massagen, oder Ähnliches) geplant oder angeraten?'
  ).withFunctionDependencies([isFragengruppeGvSparteRequired, isPersonNotImErstenLebensjahrDependencyFunction], false);

  const behandlungenGroup = new ModelGroup('gvBehandlungenGeplantWelche', '')
    .addItems(
      new StringField('welche', 'Geplante Behandlung, Operation, Untersuchung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(behandlungen.id))
        .withRemoveDataWhenNotRelevant(true),
      createStationaerAmbulantField(isFrageTrue(behandlungen.id)),
      new StringField('wann', 'Zeitraum der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(20)
        .withFunctionDependency(isFrageTrue(behandlungen.id))
        .withRemoveDataWhenNotRelevant(true),
      new StringField('wo', 'Ort der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(30)
        .withFunctionDependency(isFrageTrue(behandlungen.id))
        .withRemoveDataWhenNotRelevant(true)
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(behandlungen.id));

  return [behandlungen, behandlungenGroup];
}

/**
 * FRAGE: PFLEGEGELD
 *
 */
export function createGvPflegegeldModelItems(): ModelItem[] {
  const pflegegeld: BooleanField = new BooleanField(
    'gvPflegegeld',
    'Haben Sie jemals ein Pflegegeld bzw. eine Pflegerente bezogen?'
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const pflegegeldGroup = new ModelGroup('gvPflegegeldGrund', '')
    .addItems(
      new StringField('warum', 'Grund für den Bezug')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(pflegegeld.id))
        .withRemoveDataWhenNotRelevant(true),
      new StringField('wann', 'Zeitraum des Bezuges')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(20)
        .withFunctionDependency(isFrageTrue(pflegegeld.id))
        .withRemoveDataWhenNotRelevant(true)
    )
    .withFunctionDependency(isFrageTrue(pflegegeld.id));

  return [pflegegeld, pflegegeldGroup];
}

/**
 * FRAGE: PROFI SPORT
 *
 */
export function createGvSportModelItems(): ModelItem[] {
  const sport: BooleanField = new BooleanField(
    'gvProfiSport',
    `
      Betreiben Sie hauptberuflich Sport bzw. nehmen Sie an Landes-, Bundes- oder Internationalen Wettbewerben teil
       und erhalten ein Entgelt?
    `
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const sportGroup = new ModelGroup('gvProfiSportart', '')
    .addItems(
      new StringField('welche', 'Betriebene Sportart')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependency(isFrageTrue(sport.id))
    )
    .withFunctionDependency(isFrageTrue(sport.id));

  return [sport, sportGroup];
}

/**
 * FRAGE: MEDIKAMENTE, TABAK, ALKOHOL, DROGEN, SUCHGIFTE
 */
export function createGvSuchtgifteFrageModelItems(): ModelItem[] {
  const suchtgifte: BooleanField = new BooleanField(
    'gvSuchtgifte',
    'Nehmen Sie laufend Medikamente, Tabak, Alkohol, Drogen oder Suchtgifte?'
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const suchtgifteGroup = new ModelGroup('gvSuchtgifteWelche', '')
    .addItems(
      new StringField(
        'welche',
        'Medikament, Tabak, Alkohol, Droge, Suchtgift (Angabe zur Menge bzw. in welcher Dosis (mg))'
      )
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(suchtgifte.id))
        .withRemoveDataWhenNotRelevant(true)
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(suchtgifte.id));

  return [suchtgifte, suchtgifteGroup];
}

/**
 * FRAGE: AKTUELLE KRANKHEITEN
 *
 */
export function createGvAktuelleKrankheitenModelItems(): ModelItem[] {
  const aktuellekrankheiten: BooleanField = new BooleanField(
    'gvAktuelleKrankheiten',
    'Bestehen behandelte oder unbehandelte Krankheiten, Verletzungen, Beschwerden, Gebrechen oder sonstige Anomalien?'
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const aktuelleKrankheitenGroup = new ModelGroup('gvAktuelleKrankheitenWelche', '')
    .addItems(
      new StringField('welche', 'Krankheit, Verletzung, Beschwerden, Gebrechen, Anomalie')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(aktuellekrankheiten.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('ausgeheilt', 'Ist die Krankheit ausgeheilt?', isFrageTrue(aktuellekrankheiten.id)),
      createJaNeinField('behandelt', 'Wurde die Krankheit behandelt?', isFrageTrue(aktuellekrankheiten.id)),
      new StringField('wann', 'Zeitraum der Behandlung')
        .withMinlength(3)
        .withMaxlength(20)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(aktuellekrankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_AKTUELLE_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ]),
      new StringField('wo', 'Ort der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(30)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(aktuellekrankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_AKTUELLE_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ])
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(aktuellekrankheiten.id));

  return [aktuellekrankheiten, aktuelleKrankheitenGroup];
}

/**
 * FRAGE: KRANKHEITEN
 *
 */
export function createGvKrankheitenModelItems(): ModelItem[] {
  const krankheiten: BooleanField = new BooleanField(
    'gvKrankheiten',
    'Bestanden in den letzten 5 Jahren ambulant behandelte oder unbehandelte Krankheiten, Verletzungen, Beschwerden oder sonstige Anomalien?'
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const krankheitenGroup = new ModelGroup('gvKrankheitenWelche', '')
    .addItems(
      new StringField('welche', 'Krankheit, Verletzung, Beschwerde, Anomalie')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(krankheiten.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('ausgeheilt', 'Ist die Krankheit ausgeheilt?', isFrageTrue(krankheiten.id)),
      createJaNeinField('behandelt', 'Wurde die Krankheit behandelt?', isFrageTrue(krankheiten.id)),
      new StringField('wann', 'Zeitraum der Behandlung')
        .withMinlength(3)
        .withMaxlength(20)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(krankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ]),
      new StringField('wo', 'Ort der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(30)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(krankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ])
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(krankheiten.id));

  return [krankheiten, krankheitenGroup];
}

/**
 * FRAGE: KRANKHEITEN
 *
 */
export function createGvSchwereKrankheitenModelItems(): ModelItem[] {
  const schwereKrankheiten: BooleanField = new BooleanField(
    'gvSchwereKrankheiten',
    `
    Bestanden in den letzten 10 Jahren schwerwiegende Erkrankungen, Verletzungen oder sonstige Anomalien wie z.B. Erkrankungen der
    Atmungsorgane, des Herzens und des Kreislaufsystems, des venösen und arteriellen Gefäßsystems, der
    Verdauungsorgane, des zentralen und peripheren Nervensystems, geistig/seelische Störungen,
    der Knochen/Gelenke/Muskeln und Sehnen, der Harn- und Geschlechtsorgane, des Blutes, der Drüsen, des Stoffwechsels,
    der Haut, der Ohren, der Augen, gut- und bösartige Tumore, Infektionskrankheiten, Unfruchtbarkeit,
    Missbildungen, ein positiver HIV Befund?
    `
  ).withFunctionDependencies([isFragengruppeGvSparteRequired, isPersonNotImErstenLebensjahrDependencyFunction], false);

  const schwereKrankheitenGroup = new ModelGroup('gvSchwereKrankheitenWelche', '')
    .addItems(
      new StringField('welche', 'Schwerwiegende Krankheit, Verletzung, Anomalie')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(schwereKrankheiten.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('ausgeheilt', 'Ist die Krankheit ausgeheilt?', isFrageTrue(schwereKrankheiten.id)),
      createJaNeinField('behandelt', 'Wurde die Krankheit behandelt?', isFrageTrue(schwereKrankheiten.id)),
      new StringField('wann', 'Zeitraum der Behandlung')
        .withMinlength(3)
        .withMaxlength(20)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(schwereKrankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_SCHWERE_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ]),
      new StringField('wo', 'Ort der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(30)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(schwereKrankheiten.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_SCHWERE_KRANKHEITEN_WELCHE_BEHANDELT) // wenn behandelt
        ])
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(schwereKrankheiten.id));

  return [schwereKrankheiten, schwereKrankheitenGroup];
}

/**
 * FRAGE: STATIONAER
 *
 */
export function createGvStationaerModelItems(): ModelItem[] {
  const stationaer: BooleanField = new BooleanField(
    'gvStationaer',
    `Hat in den letzten 10 Jahren ein stationärer Krankenhaus-, Sanatoriums-, Klinik-, Heilstätten- oder Kuraufenthalt, eine Operation,
    eine Tiefenbestrahlung (Röntgen, Radium, Isotopen etc.), eine Chemotherapie stattgefunden?`
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const stationaerGroup = new ModelGroup('gvStationaerWelche', '')
    .addItems(
      new StringField('welche', 'Stationärer Aufenthalt, Operation, Tiefenbestrahlung, Chemotherapie')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(stationaer.id))
        .withRemoveDataWhenNotRelevant(true),
      new StringField('wann', 'Zeitraum der Behandlung')
        .withMinlength(3)
        .withMaxlength(20)
        .withFunctionDependency(isFrageTrue(stationaer.id))
        .withRemoveDataWhenNotRelevant(true),
      new StringField('wo', 'Ort der Behandlung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(30)
        .withFunctionDependency(isFrageTrue(stationaer.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('operation', 'Wurde eine Operation durchgeführt?', isFrageTrue(stationaer.id)),
      createJaNeinField('ausgeheilt', 'Ist die Krankheit ausgeheilt?').withFunctionDependency(
        isFrageTrue(stationaer.id)
      ),
      new StringField('seitWann', 'Ende der Erkrankung')
        .withMinlength(3)
        .withMaxlength(20)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(stationaer.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_STATIONAER_WELCHE_AUSGEHEILT) // wenn ausgeheilt
        ])
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(stationaer.id));

  return [stationaer, stationaerGroup];
}

/**
 * FRAGE: VERLETZUNGEN / UNFÄLLE / VERGIFTUNGEN
 *
 */
export function createGvVerletzungenModelItems(): ModelItem[] {
  const verletzungenField: BooleanField = new BooleanField(
    'gvVerletzungen',
    'Haben Sie Verletzungen, Unfälle oder Vergiftungen erlitten?'
  ).withFunctionDependencies(
    [
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction,
      notHasPersonTarifklasseAssistedCareBestDependencyFunction
    ],
    false
  );

  const verletzungenGroup = new ModelGroup('gvVerletzungenWelche', '')
    .addItems(
      new StringField('welche', 'Verletzung, Unfall, Vergiftung')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(verletzungenField.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('invaliditaet', 'Besteht eine dauernde Invalidität?', isFrageTrue(verletzungenField.id)),
      new StringField('invaliditaetGrad', 'Grad der dauernden Invalidität (%)')
        .withMinlength(1)
        .withMaxlength(10)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(verletzungenField.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_VERLETZUNGEN_WELCHE_INVALIDITAET) // Inval. Grad wenn Invaliditaet
        ]),
      createJaNeinField('unfallFolgen', 'Bestehen noch Unfallfolgen?', isFrageTrue(verletzungenField.id)),
      new StringField('unfallFolgenWelche', 'Unfallfolgen')
        .withMinlength(3)
        .withMaxlength(160)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(verletzungenField.id),
          createOptionJaDependencyFunction(ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_VERLETZUNGEN_WELCHE_UNFALL_FOLGEN)
        ])
    )
    .asArray()
    .withFunctionDependencies([
      isFrageTrue(verletzungenField.id),
      isFragengruppeGvSparteRequired,
      isPersonNotImErstenLebensjahrDependencyFunction
    ]);

  return [verletzungenField, verletzungenGroup];
}

/**
 * FRAGE: Kleinkind FRÜHGEBURT
 *
 */
export function createGvFruehgeburtModelField(): ModelField {
  return new BooleanField('gvFruehGeburt', 'Ist eine Frühgeburt vorgelegen?').withFunctionDependencies(
    [isFragengruppeGvSparteRequired, isPersonImErstenLebensjahrDependencyFunction],
    false
  );
}

/**
 * FRAGE: Kleinkind (< 1 LJ)
 *
 */
export function createGvKleinkindFrageModelItems(): ModelItem[] {
  const kindSonstigeKrankheiten: BooleanField = new BooleanField(
    'gvKindSonstigeKrankheiten',
    'Bestehen sonstige behandelte oder unbehandelte Erkrankungen, Anomalien oder Beschwerden?'
  ).withFunctionDependencies([isFragengruppeGvSparteRequired, isPersonImErstenLebensjahrDependencyFunction], false);

  const krankheitenGroup = new ModelGroup('gvKindSonstigeKrankheitenWelche', '')
    .addItems(
      new StringField('welche', 'Erkrankung, Anomalie, Beschwerden')
        .withRequired()
        .withMinlength(3)
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(kindSonstigeKrankheiten.id))
        .withRemoveDataWhenNotRelevant(true),
      createJaNeinField('ausgeheilt', 'Ist die Krankheit ausgeheilt?'),
      new StringField('seitWann', 'Ende der Erkrankung')
        .withMinlength(3)
        .withMaxlength(20)
        .withRemoveDataWhenNotRelevant(true)
        .withFunctionDependencies([
          isFrageTrue(kindSonstigeKrankheiten.id),
          createOptionJaDependencyFunction(
            ITEM_PERSONEN_GESUNDHEITSFRAGEN_GV_KIND_SONSTIGE_KRANKHEITEN_WELCHE_AUSGEHEILT
          ) // seitWann wenn ausgeheilt
        ])
    )
    .asArray()
    .withFunctionDependency(isFrageTrue(kindSonstigeKrankheiten.id));

  return [kindSonstigeKrankheiten, krankheitenGroup];
}

/**
 * FRAGE: WEIBLICHE VP'S ab dem 1. LJ - SCHWANGERSCHAFT
 */
export function createGvSchwangerschaftModelItems(): ModelItem[] {
  const schwangerschaft: BooleanField = new BooleanField(
    'gvSchwangerschaft',
    'Besteht eine Schwangerschaft? (Wurde im GV-Angebot bereits beantwortet)'
  )
    .withReadOnlyFunction(isGvSchwangerschaftReadOnly)
    .withFunctionDependencies(
      [
        isFragengruppeGvSparteRequired,
        isPersonWeiblichDependencyFunction,
        isPersonNotImErstenLebensjahrDependencyFunction,
        notHasPersonTarifklasseAssistedCareBestDependencyFunction
      ],
      false
    );

  const schwangerschaftGroup = new ModelGroup('gvSchwangerschaftDetail', 'Details zur Schwangerschaft')
    .addItems(
      new DateField('gvSchwangerschaftGeburtstermin', 'Geburtstermin')
        .withFunctionDependency(isFrageTrue(schwangerschaft.id))
        .withRemoveDataWhenNotRelevant(true),
      new StringField(
        'gvSchwangerschaftGeburtenInfo',
        'Anzahl der vorausgehenden Geburten (Angabe von Risikogeburten, Fehlgeburten)'
      )
        .withMaxlength(160)
        .withFunctionDependency(isFrageTrue(schwangerschaft.id))
        .withRemoveDataWhenNotRelevant(true)
    )
    .withFunctionDependency(isFrageTrue(schwangerschaft.id), false);

  return [schwangerschaft, schwangerschaftGroup];
}

export function isGvSchwangerschaftReadOnly(modelItem: ModelItem, modelContext: IModelContext): boolean {
  const gesundheitsfragen: Gesundheitsfragen = modelContext.dataStack.at(-1);
  return gesundheitsfragen && gesundheitsfragen.schwangerInfoUebernommen;
}

function createStationaerAmbulantField(dependencyFunction: DependencyFunction): NumberField {
  return new NumberField('behandlungStationaerOderAmbulant', 'Welche Behandlung der Krankheit ist geplant?')
    .withOptions(new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(stationaerAmbulantOptions))
    .withFunctionDependency(dependencyFunction)
    .withRemoveDataWhenNotRelevant();
}
